import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, ImageOff } from "lucide-react";

export default function CollectibleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const [collectible, setCollectible] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    Promise.all([api.get(`/collectibles/${id}`), api.get("/categories")])
      .then(([cRes, catRes]) => {
        setCollectible(cRes.data);
        setCategories(catRes.data);
        setActivePhoto(0);
      })
      .catch(() => setCollectible(null))
      .finally(() => setLoading(false));
  }, [id]);

  const isOwner = user && collectible?.owner_id && user.user_id === collectible.owner_id;
  // Owners edit in Studio; admins edit shared (ownerless) items in Admin.
  const canManage = isOwner || (collectible && !collectible.owner_id && isAdmin);
  const manageTo = isOwner ? "/studio" : "/admin";

  const categoryName = collectible?.category_id
    ? categories.find((c) => c.id === collectible.category_id)?.name
    : null;

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <div className="glow-bg" />
        <Header />
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-2 border-glow-primary border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(0,240,255,0.4)]" />
        </div>
      </div>
    );
  }

  if (!collectible) {
    return (
      <div className="relative min-h-screen">
        <div className="glow-bg" />
        <Header />
        <div className="relative z-10 pt-40 text-center">
          <p className="font-heading text-2xl text-white uppercase tracking-widest">Not Found</p>
          <Link to="/" className="font-mono text-glow-primary text-sm mt-4 inline-block">
            &larr; Back to Gallery
          </Link>
        </div>
      </div>
    );
  }

  const photos = collectible.photos || [];
  const current = photos[activePhoto];

  return (
    <div className="relative min-h-screen">
      <div className="glow-bg" />
      <Header />

      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <div className="flex items-center justify-between mb-10">
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-white/60 hover:text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono text-xs uppercase tracking-wider pl-0"
            data-testid="back-to-gallery-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Vault
          </Button>
          {canManage && (
            <Link to={manageTo} state={{ editId: collectible.id }}>
              <Button
                variant="outline"
                className="border-glow-primary/50 text-glow-primary hover:bg-glow-primary/10 rounded-none font-mono text-xs uppercase tracking-wider"
                data-testid="edit-from-detail-btn"
              >
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </Button>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left: sticky photos */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <motion.div
              key={activePhoto}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="aspect-square w-full bg-surface border border-white/10 overflow-hidden shadow-[0_0_40px_rgba(0,240,255,0.08)]"
              data-testid="detail-main-photo"
            >
              {current ? (
                <img
                  src={fileUrl(current.storage_path)}
                  alt={collectible.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20">
                  <ImageOff className="w-12 h-12" />
                </div>
              )}
            </motion.div>

            {photos.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto no-scrollbar" data-testid="photo-thumbnails">
                {photos.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setActivePhoto(i)}
                    aria-label={`View photo ${i + 1}`}
                    className={`shrink-0 w-20 h-20 border overflow-hidden transition-[border-color,box-shadow] duration-200 ${
                      i === activePhoto
                        ? "border-glow-primary shadow-[0_0_12px_rgba(0,240,255,0.4)]"
                        : "border-white/10 hover:border-white/40"
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={fileUrl(p.storage_path)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: details */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-primary">
                  Artifact Dossier
                </span>
                {categoryName && (
                  <span className="font-mono text-[10px] uppercase tracking-wider text-glow-accent border border-glow-accent/40 px-2 py-0.5" data-testid="detail-category">
                    {categoryName}
                  </span>
                )}
              </div>
              <h1
                className="font-heading font-900 text-4xl md:text-5xl tracking-tighter leading-tight text-white mt-4"
                data-testid="detail-title"
              >
                {collectible.title}
              </h1>

              <div className="mt-8 flex items-center gap-6 border-y border-white/10 py-4 font-mono text-xs text-white/50 uppercase tracking-wider">
                <span data-testid="detail-photo-count">{photos.length} Photos</span>
                <span className="w-px h-4 bg-white/10" />
                <span>ID: {collectible.id.slice(0, 8)}</span>
              </div>

              <p
                className="mt-8 font-mono text-sm md:text-base text-white/70 leading-relaxed whitespace-pre-wrap"
                data-testid="detail-description"
              >
                {collectible.description}
              </p>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
}
